import { useEffect, useState } from "react";
import { api } from "./api.js";
import { tr } from "./i18n.js";
import { data as dataScurta, dataOra } from "./text.js";
import {
  CEASURI_SLA, eticheta, MODURI_ACOPERIRE, STARI_LA_PLECARE, STARI_TICHET, STARI_VIZITA,
} from "./enums.js";

/**
 * Dosarul intervenției — ce s-a întâmplat de la sesizare până la închidere,
 * peste TOATE vizitele.
 *
 * Fișa rămâne documentul unei vizite: se semnează de clientul care a văzut ce
 * s-a lucrat atunci. Dosarul e ce lipsea deasupra ei — o defecțiune rezolvată
 * în trei drumuri lăsa clientul cu trei procese-verbale separate și cu nicio
 * propoziție care să spună ce a fost și cum s-a rezolvat.
 */
function ore(minute) {
  if (!minute) return "0h";
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  // „0h 27m" nu se citește ca 27 de minute — lecție deja plătită pe portal.
  return h ? `${h}h${m ? ` ${m}m` : ""}` : `${m}m`;
}

export default function DosarInterventie({ tichetId, laInchidere }) {
  const [dosar, setDosar] = useState(null);
  const [eroare, setEroare] = useState(null);

  useEffect(() => {
    let viu = true;
    api.dosarInterventie(tichetId)
      .then((d) => { if (viu) setDosar(d); })
      .catch((e) => { if (viu) setEroare(e?.message || tr("Dosarul nu s-a putut deschide.")); });
    return () => { viu = false; };
  }, [tichetId]);

  if (eroare) return <div className="modal-fundal"><div className="modal"><p className="eroare">{eroare}</p>
    <div className="modal-actiuni"><button className="buton" onClick={laInchidere}>{tr("Închide")}</button></div></div></div>;
  if (!dosar) return <div className="modal-fundal"><div className="modal"><p>{tr("Se încarcă…")}</p></div></div>;

  const { tichet, vizite, rezumat, ceasuri } = dosar;

  return (
    <div className="modal-fundal" onClick={(e) => { if (e.target === e.currentTarget) laInchidere(); }}>
      <div className="modal modal-lat">
        <h3>{tr("Dosarul intervenției")} <code className="cod-mic">{tichet.numar}</code></h3>

        {/*
          * Un dosar deschis pe un tichet încă deschis e legitim — dispecerul
          * vrea să vadă unde s-a ajuns — dar spune pe față că nu e documentul
          * final, ca nimeni să nu-l trimită clientului drept încheiere.
          */}
        {!dosar.definitiv && (
          <p className="nota">
            {tr("Intervenția e încă deschisă. Dosarul arată unde s-a ajuns, nu e documentul final.")}
          </p>
        )}

        <div className="panou">
          <div className="panou-antet">{tr("Sesizarea")}</div>
          <div className="panou-corp">
            <p><b>{tichet.titlu}</b></p>
            <p>{tichet.descriere}</p>
            <p className="nota">
              {tichet.client?.denumire} · {tichet.locatie?.denumire}
              {tichet.sistemInstalat ? ` · ${tichet.sistemInstalat.denumire}` : ""}
            </p>
            <p className="nota">
              {tr("Deschis")}: {dataOra(tichet.deschisLa)}
              {tichet.inchisLa ? ` · ${tr("închis")}: ${dataOra(tichet.inchisLa)}` : ""}
              {` · ${eticheta(STARI_TICHET, tichet.status)}`}
            </p>
            {tichet.motivInchidere && (
              <p className="nota">{tr("Închis fără intervenție")}: {tichet.motivInchidere}</p>
            )}
          </div>
        </div>

        <div className="panou">
          <div className="panou-antet">{tr("Pe scurt")}</div>
          <div className="panou-corp">
            <p>
              {tr("Vizite")}: <b>{rezumat.numarVizite}</b>
              {" · "}{tr("timp lucrat")}: <b>{ore(rezumat.minuteLucru)}</b>
              {" · "}{tr("deplasare")}: <b>{ore(rezumat.minuteDeplasare)}</b>
            </p>
            {rezumat.numarFiseStornate > 0 && (
              <p className="nota">
                {tr("Din care stornate")}: {rezumat.numarFiseStornate}
                {" — "}{tr("rămân în dosar, dar nu intră în totaluri.")}
              </p>
            )}
            {rezumat.piese.length > 0 ? (
              <div className="tabel-derulabil">
                <table className="tabel">
                  <thead><tr>
                    <th>{tr("Piesă")}</th><th>{tr("Cantitate")}</th><th>{tr("Serii")}</th>
                  </tr></thead>
                  <tbody>
                    {rezumat.piese.map((p) => (
                      <tr key={p.cod}>
                        <td><code>{p.cod}</code> {p.denumire}</td>
                        <td>{p.cantitate} {p.unitate}</td>
                        <td>{p.serii.join(", ") || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="nota">{tr("Nicio piesă schimbată.")}</p>}
          </div>
        </div>

        {ceasuri.length > 0 && (
          <div className="panou">
            <div className="panou-antet">{tr("Termenele asumate")}</div>
            <div className="panou-corp">
              {ceasuri.map((c, i) => (
                <p key={i} className="nota">
                  {eticheta(CEASURI_SLA, c.tip)} · {tr("termen")} {dataOra(c.termenLa)} ·{" "}
                  {c.incadratInTermen === null
                    ? tr("încă în curs")
                    : c.incadratInTermen ? tr("încadrat în termen") : tr("depășit")}
                </p>
              ))}
            </div>
          </div>
        )}

        <div className="panou">
          <div className="panou-antet">{tr("Vizitele, în ordine")}</div>
          <div className="panou-corp">
            {vizite.length === 0 && <p className="nota">{tr("Nicio vizită încă.")}</p>}
            {vizite.map((v, i) => (
              <div key={v.id} className="bloc-vizita">
                <p>
                  <b>{tr("Vizita")} {i + 1}</b> <code className="cod-mic">{v.numar}</code>
                  {" · "}{dataScurta(v.dataProgramata)}
                  {" · "}{eticheta(STARI_VIZITA, v.status)}
                  {v.tehnicianPrincipal ? ` · ${v.tehnicianPrincipal.numeComplet}` : ""}
                  {v.echipa ? ` · ${v.echipa.denumire}` : ""}
                </p>
                {!v.fisa && <p className="nota">{tr("Fără fișă de intervenție.")}</p>}
                {v.fisa && (
                  <>
                    <p className="nota">
                      <code className="cod-mic">{v.fisa.numar}</code>
                      {v.fisa.durataMinute ? ` · ${ore(v.fisa.durataMinute)}` : ""}
                      {v.fisa.numeSemnatarClient
                        ? ` · ${tr("semnat de")} ${v.fisa.numeSemnatarClient}` : ""}
                      {v.fisa.status === "Stornata"
                        ? ` · ${tr("STORNATĂ")}${v.fisa.motivStornare ? `: ${v.fisa.motivStornare}` : ""}`
                        : ""}
                    </p>
                    {v.fisa.descriereConstatari && (
                      <p>{tr("Constatat")}: {v.fisa.descriereConstatari}</p>
                    )}
                    {v.fisa.remedieriEfectuate && (
                      <p>{tr("Remediat")}: {v.fisa.remedieriEfectuate}</p>
                    )}
                    {v.fisa.cauzaRadacina && (
                      <p>{tr("Cauza")}: {v.fisa.cauzaRadacina}</p>
                    )}
                    {v.fisa.operatii.length > 0 && (
                      <ul>
                        {v.fisa.operatii.map((o) => (
                          <li key={o.id}>
                            <code>{o.operatie.cod}</code> {o.operatie.denumire}
                            {" — "}{ore(o.timpRealMinute)}
                            {" · "}{eticheta(MODURI_ACOPERIRE, o.modAcoperire)}
                          </li>
                        ))}
                      </ul>
                    )}
                    {v.fisa.stareLaPlecare && (
                      <p className="nota">
                        {tr("La plecare")}: {eticheta(STARI_LA_PLECARE, v.fisa.stareLaPlecare)}
                        {v.fisa.necesitaRevenire
                          ? ` · ${tr("cere revenire")}${v.fisa.motivRevenire ? `: ${v.fisa.motivRevenire}` : ""}`
                          : ""}
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="modal-actiuni">
          <button className="buton" onClick={laInchidere}>{tr("Închide")}</button>
        </div>
      </div>
    </div>
  );
}
